import { Fingerprint, Tick } from './LoginBits';

// ────────────────────────────────────────────────────────────────────────────
// MfaOverlay · modal full-screen sobre el login mientras corre biometría o
// pide TOTP. Reemplaza al redirect a /auth/mfa-challenge para sesión fluida.
// ────────────────────────────────────────────────────────────────────────────

export type MfaModalState =
  | { kind: 'closed' }
  | { kind: 'bio' } // mostrando "Activando datos biométricos…"
  | {
      kind: 'totp';
      factorId: string;
      reason: 'no-passkey' | 'bio-failed' | 'bio-cancelled';
      /**
       * La cuenta tiene passkeys pero este navegador no se sabe que tenga la
       * suya: la biometría no se dispara sola (en Chrome abriría el QR del
       * celular) y se ofrece como botón.
       */
      puedePasskey?: boolean;
    };

type MfaOverlayProps = {
  state: Exclude<MfaModalState, { kind: 'closed' }>;
  totpCode: string;
  setTotpCode: (v: string) => void;
  totpSubmitting: boolean;
  onTotpSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  /** Probar la biometría a mano (solo cuando `state.puedePasskey`). */
  onUsarPasskey?: () => void;
};

export function MfaOverlay({
  state,
  totpCode,
  setTotpCode,
  totpSubmitting,
  onTotpSubmit,
  onCancel,
  onUsarPasskey,
}: MfaOverlayProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        background: 'rgba(7, 11, 14, 0.78)',
        backdropFilter: 'blur(10px) saturate(140%)',
        WebkitBackdropFilter: 'blur(10px) saturate(140%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 22,
        animation: 'dd-overlay-in .25s ease',
      }}
    >
      <style>{`
        @keyframes dd-overlay-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes dd-bio-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.06); opacity: 0.85; }
        }
        @keyframes dd-bio-ring {
          0% { transform: scale(0.85); opacity: 0.6; }
          100% { transform: scale(1.6); opacity: 0; }
        }
      `}</style>

      <div
        style={{
          width: 'min(380px, 100%)',
          padding: 28,
          background: 'rgba(10, 16, 21, 0.94)',
          border: '1px solid rgba(102, 224, 255, 0.32)',
          borderRadius: 10,
          boxShadow: `
            0 0 0 1px rgba(102, 224, 255, 0.08),
            0 32px 80px -28px rgba(0,0,0,0.95),
            0 0 100px -20px rgba(102, 224, 255, 0.22)
          `,
          fontFamily: 'Inter, system-ui, sans-serif',
          position: 'relative',
        }}
      >
        <Tick pos="tl" />
        <Tick pos="tr" />
        <Tick pos="bl" />
        <Tick pos="br" />

        {state.kind === 'bio' ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 18,
              padding: '12px 0 4px',
            }}
          >
            {/* Pulso biométrico */}
            <div
              style={{
                position: 'relative',
                width: 84,
                height: 84,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  border: '1.5px solid #66e0ff',
                  animation: 'dd-bio-ring 1.6s ease-out infinite',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  border: '1.5px solid #66e0ff',
                  animation: 'dd-bio-ring 1.6s ease-out .55s infinite',
                }}
              />
              <span
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background:
                    'radial-gradient(circle, rgba(102,224,255,0.28) 0%, rgba(102,224,255,0.08) 65%, transparent 100%)',
                  border: '1px solid rgba(102, 224, 255, 0.55)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'dd-bio-pulse 1.6s ease-in-out infinite',
                }}
              >
                <Fingerprint />
              </span>
            </div>

            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: 10,
                  letterSpacing: '0.2em',
                  color: 'rgba(102, 224, 255, 0.85)',
                  textTransform: 'uppercase',
                  marginBottom: 8,
                }}
              >
                {'// acceso biométrico'}
              </div>
              <h3
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#f6fbff',
                  margin: '0 0 6px',
                  letterSpacing: '-0.01em',
                }}
              >
                Activando datos biométricos…
              </h3>
              <p
                style={{
                  fontSize: 12.5,
                  color: 'rgba(255,255,255,0.55)',
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                Autorizá con Touch ID, Face ID o tu huella cuando aparezca el
                aviso del sistema.
              </p>
            </div>

            <button
              type="button"
              onClick={onCancel}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'rgba(255,255,255,0.4)',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 10,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                marginTop: 4,
              }}
            >
              Cancelar
            </button>
          </div>
        ) : (
          <form onSubmit={onTotpSubmit} noValidate>
            <div style={{ marginBottom: 14 }}>
              <div
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: 10,
                  letterSpacing: '0.18em',
                  color: 'rgba(102, 224, 255, 0.85)',
                  textTransform: 'uppercase',
                  marginBottom: 8,
                }}
              >
                {'// TOTP · 6 dígitos'}
              </div>
              <h3
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#f6fbff',
                  margin: '0 0 6px',
                  letterSpacing: '-0.01em',
                }}
              >
                {state.reason === 'bio-cancelled'
                  ? 'Verificación cancelada'
                  : state.reason === 'bio-failed'
                    ? 'Biometría no disponible'
                    : 'Verificación 2FA'}
              </h3>
              <p
                style={{
                  fontSize: 12.5,
                  color: 'rgba(255,255,255,0.55)',
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                {state.reason === 'bio-cancelled'
                  ? 'Cancelaste la verificación biométrica. Ingresá el código de tu app autenticadora.'
                  : state.reason === 'bio-failed'
                    ? 'Este navegador no pudo usar tu passkey. Ingresá el código de tu app autenticadora; después podés activar la entrada rápida en este dispositivo.'
                    : 'Ingresá el código de tu app autenticadora.'}
              </p>
            </div>

            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoFocus
              autoComplete="one-time-code"
              placeholder="000000"
              value={totpCode}
              disabled={totpSubmitting}
              onChange={(e) =>
                setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              style={{
                width: '100%',
                padding: '14px 16px',
                background: 'rgba(7, 11, 14, 0.6)',
                border: '1px solid rgba(102, 224, 255, 0.32)',
                borderRadius: 6,
                color: '#f6fbff',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 22,
                letterSpacing: '0.3em',
                textAlign: 'center',
                outline: 'none',
                marginBottom: 14,
              }}
            />

            <button
              type="submit"
              disabled={totpSubmitting || totpCode.length !== 6}
              className="dd-enter-btn"
              aria-busy={totpSubmitting}
              style={{ width: '100%' }}
            >
              {totpSubmitting ? (
                <>
                  <span className="dd-spinner" aria-hidden="true" />
                  <span>Verificando…</span>
                </>
              ) : (
                <>
                  <span>Verificar</span>
                  <span aria-hidden="true" style={{ fontSize: 14 }}>
                    →
                  </span>
                </>
              )}
            </button>

            {state.puedePasskey && onUsarPasskey && (
              <button
                type="button"
                className="dd-link-btn"
                onClick={onUsarPasskey}
                disabled={totpSubmitting}
                style={{ display: 'block', margin: '14px auto 0' }}
              >
                Usar huella / Face ID
              </button>
            )}

            <button
              type="button"
              onClick={onCancel}
              disabled={totpSubmitting}
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                color: 'rgba(255,255,255,0.4)',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 10,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                marginTop: 12,
                padding: 8,
              }}
            >
              Cancelar y salir
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
